import { OrderStatus, OrderStatusDescription, OrderType, OrderTypeDescription } from "../../../models/order";
import { OrderTimingReport, OrderTimingStage, OrderTimingStats } from "../../../models/orderAnalytics";
import { toNum } from "../../bids/analytics/format";
import { ordersListUrl } from "./drilldown";

const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 60 * SECONDS_IN_MINUTE;
const SECONDS_IN_DAY = 24 * SECONDS_IN_HOUR;
/** До двух суток возраст удобнее читать в часах */
const AGE_HOURS_LIMIT = 48;

const STAGE_LABELS: Record<OrderTimingStage, string> = {
    [OrderTimingStage.CreatedToRequested]: "Создан → запрос продавцу",
    [OrderTimingStage.RequestedToReceived]: "Запрос продавцу → получено ботом",
    [OrderTimingStage.ReceivedToSentToBuyer]: "Получено ботом → отправлено покупателю",
    [OrderTimingStage.SentToBuyerToCompleted]: "Отправлено покупателю → завершён",
    [OrderTimingStage.Total]: "Всего: создан → завершён",
};

function ru(value: number): string {
    return value.toLocaleString("ru-RU", { maximumFractionDigits: 1 });
}

/** Длительность в секундах: «< 1 сек», «35 сек», «58 мин», «3,2 ч», «1,5 д»; без значения — прочерк */
export function formatSeconds(seconds: number | null | undefined): string {
    if (seconds === null || seconds === undefined) {
        return "—";
    }
    const s = toNum(seconds);
    if (s < 1) {
        return "< 1 сек";
    }
    if (s < SECONDS_IN_MINUTE) {
        return `${ru(s)} сек`;
    }
    if (s < SECONDS_IN_HOUR) {
        return `${ru(s / SECONDS_IN_MINUTE)} мин`;
    }
    if (s < SECONDS_IN_DAY) {
        return `${ru(s / SECONDS_IN_HOUR)} ч`;
    }
    return `${ru(s / SECONDS_IN_DAY)} д`;
}

/** Возраст зависшего заказа: до двух суток — часы, дальше — дни */
export function formatAgeHours(hours: number): string {
    const h = toNum(hours);
    return h < AGE_HOURS_LIMIT ? `${ru(h)} ч` : `${ru(h / 24)} д`;
}

export function stageLabel(stage: OrderTimingStage): string {
    return STAGE_LABELS[stage] ?? `Этап ${stage}`;
}

/** Ячейка разреза: «медиана / p90», без заказов — прочерк */
export function stageCell(stats: OrderTimingStats | undefined): string {
    if (!stats || toNum(stats.count) === 0) {
        return "—";
    }
    return `${formatSeconds(stats.medianSeconds)} / ${formatSeconds(stats.p90Seconds)}`;
}

export type TimingSlice = { key: string; label: string; stages: OrderTimingStats[] };

/** Срезы для выбора над таблицей этапов: все ордера, затем каждый тип сделки и каждая игра */
export function timingSlices(report: OrderTimingReport): TimingSlice[] {
    return [
        { key: "all", label: "Все ордера", stages: report.stages },
        ...report.byType.map((t) => ({
            key: `type-${t.type}`, label: `Тип сделки: ${orderTypeLabel(t.type)}`, stages: t.stages,
        })),
        ...report.byGame.map((g) => ({ key: `game-${g.gameId}`, label: `Игра: ${g.gameName}`, stages: g.stages })),
    ];
}

export function orderTypeLabel(type: OrderType): string {
    return (OrderTypeDescription as Record<number, string>)[type] ?? `Тип ${type}`;
}

export function orderStatusLabel(status: OrderStatus): string {
    return (OrderStatusDescription as Record<number, string>)[status] ?? `Статус ${status}`;
}

/** Статус зависшего ордера; ManualMode без причины — новый заказ продавца, ждущий ручного запуска обработки */
export function stuckStatusLabel(status: OrderStatus, isManualStart: boolean): string {
    return isManualStart && status === OrderStatus.ManualMode
        ? "Ручной режим: ждёт запуска продавцом"
        : orderStatusLabel(status);
}

/**
 * Ссылка со счётчика зависших: список ордеров со статусом и игрой; конец периода — не позже «сейчас − порог»,
 * чтобы в список попали те же ордера, что в счётчике (зависшие = созданные раньше порога)
 */
export function stuckStatusUrl(status: OrderStatus, ctx: { gameId?: number; from: Date; to: Date },
                               stuckHours: number, now: Date = new Date()): string {
    const createdBefore = new Date(now.getTime() - stuckHours * 60 * 60 * 1000);
    const to = createdBefore < ctx.to ? createdBefore : ctx.to;
    return ordersListUrl({ status, gameId: ctx.gameId, from: ctx.from, to });
}
